import { useMemo } from "react";
import { createArray } from "../util/helpers";

export type PageProps = {
    /** Required field */
    page: number;
    /** Required field */
    totalRecords: number;
    /** Required field */
    pageSize: number;
    /** Required field */
    onPageChange: (page: number) => void;
    pageShowCount?: number;
}

type UsePaginateProps = {
    type: 'load-more' | 'stepper' | 'complex';
} & PageProps;

type PaginationReturnType = {
    nextPage: () => void;
    previousPage: () => void;
    pageNumbers: number[];
    pages: number;
    nextPageDisabled?: boolean;
    previousPageDisabled?: boolean;
    jumpToPage?: (page: number) => void;
}

export default function usePaginate(props: UsePaginateProps): PaginationReturnType {

    const { page = 1, totalRecords, pageSize, onPageChange, type, pageShowCount = 4 } = props;

    if (totalRecords === 0 && ['stepper', 'complex'].includes(type)) {
        throw new Error('Total record can not be 0 in stepper and complex type pagination');
    }

    if (totalRecords > 0 && pageSize > totalRecords && type === 'load-more') {
        console.error("totalRecords should be greater than pageSize");
    }

    const pagination = useMemo(() => {

        const pages = Math.ceil(totalRecords / pageSize);
        const pageBlockCount = Math.ceil(pages / pageShowCount);

        const pageArray = createArray(pages);

        const pageBlocks = [];
        let currentPageBlock = 1;

        for (let i = 0; i < pageBlockCount; i++) {
            const start = i * pageShowCount;
            const end = start + pageShowCount;
            const a = pageArray.slice(start, end);

            if (a.includes(page)) {
                currentPageBlock = i;
            }
            pageBlocks.push(a);
        }

        const isInfinitePagination = type === 'load-more' && totalRecords === 0;

        const nextPage = () => {
            if (isInfinitePagination) {
                onPageChange && onPageChange(page + 1);
            } else if (page < pages) {
                onPageChange && onPageChange(page + 1);
            }
        };

        const previousPage = () => {
            if (page > 1) {
                onPageChange && onPageChange(page - 1)
            }
        };

        const jumpToPage = (page: number) => {
            if ((page > pages || page < 1) && type === 'complex') {
                console.error("Page number is out of range");
            } else {
                onPageChange && onPageChange(page);
            }
        }

        const nextPageDisabled = pages === page;
        const previousPageDisabled = page === 1;
        const pageNumbers = pageBlocks[currentPageBlock];

        /** Keeping it for future reference */
        // if (type === 'complex') {
        //     const pl = pageNumbers.length;
        //     if (pl < pageShowCount) {
        //         const rem = pageShowCount - pl;
        //         const prevPageBlock = pageBlocks[currentPageBlock - 1];
        //         const start = pageBlocks[currentPageBlock - 1].length - pl;
        //         const qp = prevPageBlock.slice(start, pageShowCount);
        //         console.log({ start, pageBlocks, pl, rem, currentPageBlock, prevPageBlock, qp })
        //         pageNumbers = [...qp, ...pageNumbers]
        //     }
        // }
        /** Keeping it for future reference */

        return { pageNumbers, nextPage, previousPage, pages, previousPageDisabled, nextPageDisabled, jumpToPage };

    }, [page, totalRecords, pageSize, onPageChange, pageShowCount]);

    return pagination;
}
